/* eslint-disable jsx-a11y/label-has-associated-control */
import * as Yup from 'yup';
import React from 'react';
import { Icon } from '@iconify/react';
import { useFormik, FormikProvider } from 'formik';
import saveIcon from '@iconify/icons-eva/save-outline';

// material
import { Stack, TextField, Autocomplete } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { editItem } from '../../fetch';
import { reportTripsActions } from '../../store';
import updateFlightList from './updateFlightList';
import { useTranslation } from '../../common/LocalizationProvider';
import { selectReportTripsArray, selectSession, selectUsersArray } from '../../createSelectorUtils';

// ----------------------------------------------------------------------

FlightListAssignForm.propTypes = {
  deviceId: PropTypes.number,
  groupId: PropTypes.number,
  startTime: PropTypes.string,
  endTime: PropTypes.string
};

export default function FlightListAssignForm({ deviceId, groupId, startTime, endTime }) {
  const t = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const session = useSelector(selectSession);
  const permission = session.permission
    ? session.permission.find((item) => item.groupid === groupId) || {}
    : {};
  const reportTrip = useSelector(selectReportTripsArray).find(
    (item) => item.deviceId === deviceId && item.startTime === startTime && item.endTime === endTime
  );

  const users = useSelector(selectUsersArray).filter((user) =>
    groupId === 0 && session.administrator
      ? user
      : user.permission && user.permission.find((item) => item.groupid === groupId)
  );

  const RegisterSchema = Yup.object().shape({
    piloteId: Yup.number().required(t('yupValid_Required'))
  });

  const formik = useFormik({
    initialValues: {
      id: reportTrip.affectationVolsId,
      deviceId: reportTrip.deviceId,
      piloteId: reportTrip.driverUniqueId,
      instructeurId: reportTrip.instructeurUniqueId,
      startTime: reportTrip.startTime,
      endTime: reportTrip.endTime,
      groupId: reportTrip.groupId,
      attributes: {},
      comment: reportTrip.commentAffectationVols,
      flag: reportTrip.flagAffectationVols,
      favorite: reportTrip.favoriteAffectationVols,
      remarquableTrace: reportTrip.pointRemarquableTrace > 0
    },
    validationSchema: RegisterSchema,
    onSubmit: async (values) => {
      const response = await editItem(
        'affectationVols',
        values.id === 0 ? undefined : values.id,
        values
      );
      if (response.ok) {
        enqueueSnackbar(values.id === 0 ? t('FL_Assign_Snackbar') : t('FL_Assign_Snackbar'), {
          variant: 'success'
        });
        dispatch(reportTripsActions.update(await updateFlightList(await response.json())));
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
      }
      setSubmitting(false);
      navigate('/flight/list', { replace: true });
    }
  });
  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setSubmitting,
    setFieldValue
  } = formik;

  return (
    <FormikProvider value={formik}>
      <Stack spacing={3}>
        <Autocomplete
          fullWidth
          {...getFieldProps('piloteId')}
          options={
            permission.status === 3
              ? [session]
              : users.filter((user) => user.id !== getFieldProps('instructeurId').value)
          }
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {`${option.prenom} ${option.name}`}
            </li>
          )}
          getOptionLabel={(option) =>
            option.name === undefined ? '' : `${option.prenom} ${option.name}`
          }
          value={users.find((user) => user.id === getFieldProps('piloteId').value) || null}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('fields_Pilot')}
              error={Boolean(touched.clpiloteIdub && errors.piloteId)}
              helperText={touched.piloteId && errors.piloteId}
            />
          )}
          onChange={(event, value) => setFieldValue('piloteId', value !== null ? value.id : 0)}
        />

        <Autocomplete
          fullWidth
          {...getFieldProps('instructeurId')}
          options={users.filter(
            (user) =>
              user.statut !== 'pilote' &&
              user.statut !== 'pilote+' &&
              getFieldProps('piloteId').value !== user.id
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {`${option.prenom} ${option.name}`}
            </li>
          )}
          getOptionLabel={(option) =>
            option.name === undefined ? '' : `${option.prenom} ${option.name}`
          }
          value={users.find((user) => user.id === getFieldProps('instructeurId').value) || null}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('fields_Instructor')}
              error={Boolean(touched.instructeurId && errors.instructeurId)}
              helperText={touched.instructeurId && errors.instructeurId}
            />
          )}
          onChange={(event, value) => setFieldValue('instructeurId', value !== null ? value.id : 0)}
        />

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          startIcon={<Icon icon={saveIcon} />}
          onClick={handleSubmit}
        >
          {t('btn_Save')}
        </LoadingButton>
      </Stack>
    </FormikProvider>
  );
}
